import React from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import PathWay from "commons/components/layout/PathWay";

import { useLayoutContext } from "commons/context/layout";
import { useStandardWidthContext } from "commons/context/standardWidth";

import { RouteType } from "commons/types/route";

import { makeUrl } from "commons/utils/http";

type PropsType = {
    readonly route: RouteType;
    readonly defaultTitle: string;
    readonly useDynamicTitle?: boolean;
    readonly useNavigateBack?: boolean;
    readonly footerPageContent?: JSX.Element;
    readonly maxPathWayElements?: number;
};

function MenuBackButton({
    useNavigateBack,
    previousPath,
    baseUiUrl,
}: {
    useNavigateBack?: boolean;
    previousPath: string;
    baseUiUrl?: string;
}) {
    return (
        <>
            {!useNavigateBack && previousPath !== "" && (
                <div>
                    <Link to={`${baseUiUrl ?? ""}${previousPath}`}>
                        <ArrowBackIosIcon />
                        <span>{"Indietro"}</span>
                    </Link>
                </div>
            )}
        </>
    );
}

function BackButton({
    useNavigateBack,
    previousPath,
}: {
    useNavigateBack?: boolean;
    previousPath: string;
}) {
    const navigate = useNavigate();

    return (
        <>
            {useNavigateBack && previousPath !== "" && (
                <div>
                    <button onClick={() => navigate(-1)}>
                        <ArrowBackIosIcon />
                        <span>{"Indietro"}</span>
                    </button>
                </div>
            )}
        </>
    );
}

function PageLayout({
    route,
    defaultTitle,
    useDynamicTitle,
    useNavigateBack,
    footerPageContent,
    maxPathWayElements,
}: PropsType) {
    const standardWidthContext = useStandardWidthContext();

    let previousPath = "";

    let params = useParams();

    if (route.dontShowBackButton !== true && route.pathwayElements.length > 1) {
        const penultimateElement = route.pathwayElements[route.pathwayElements.length - 2];

        if (penultimateElement.path) {
            previousPath = makeUrl(penultimateElement.path, params);
        }
    }

    const { baseUiUrl } = useLayoutContext();

    React.useEffect(() => {
        if (useDynamicTitle) {
            const labelList: Array<string> = [];

            route.pathwayElements.forEach((element, index) => {
                if (index !== 0) {
                    labelList.push(element.label);
                }
            });

            document.title = `${route.pathwayElements[0].label}${
                labelList.length ? " • " : ""
            }${labelList.join(" / ")}`;
        } else {
            document.title = defaultTitle;
        }
    }, [useDynamicTitle, route.pathwayElements, defaultTitle]);

    return (
        <>
            {route.headerImage}

            <div className={`page ${route.fullWidth ? " full-width " : ""}`}>
                {!route.dontShowPathWay && (
                    <div className={"pathway-bar"}>
                        <div className={"layout-spacer previous-page-container"}>
                            <BackButton
                                useNavigateBack={useNavigateBack}
                                previousPath={previousPath}
                            />

                            <MenuBackButton
                                useNavigateBack={useNavigateBack}
                                previousPath={previousPath}
                                baseUiUrl={baseUiUrl}
                            />
                        </div>
                        <div
                            className={`standard-width ${route.extendedWidth ? " extended " : ""} ${
                                route.maxWidthUnset ? "max-width-unset" : ""
                            } ${
                                standardWidthContext[route.path] !== undefined &&
                                standardWidthContext[route.path] === false
                                    ? "second-level"
                                    : ""
                            }`}
                        >
                            <PathWay
                                elements={route.pathwayElements}
                                maxPathWayElements={maxPathWayElements}
                            />
                        </div>
                        <div className={"layout-spacer"}></div>
                    </div>
                )}

                <div className={"main-content"}>
                    <div className={"layout-spacer"}></div>

                    <div
                        className={`standard-width ${route.extendedWidth ? " extended " : ""} ${
                            route.maxWidthUnset ? "max-width-unset" : ""
                        } ${
                            standardWidthContext[route.path] !== undefined &&
                            standardWidthContext[route.path] === false
                                ? "second-level"
                                : ""
                        }`}
                    >
                        {route.element}
                    </div>

                    <div className={"layout-spacer"}></div>
                </div>
            </div>

            {footerPageContent}
        </>
    );
}

export default PageLayout;
